import React, { useState, useEffect } from "react";
import { api } from "../../api";

// Shared two-sided review UI: star display, rating summary, a write-review
// modal (used by both roles), and the landlord's "check tenant reputation"
// lookup. Real glyphs (★ 🔍 ✓) are used directly — these are JSX text, not
// escapes.

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const card = (h) => ({ background: h.bg, borderRadius: 18, width: "100%", maxWidth: 380, padding: 22, boxShadow: "0 12px 48px rgba(0,0,0,0.25)", maxHeight: "88vh", overflowY: "auto" });

export function Stars({ value = 0, size = 14 }) {
  const full = Math.round(Number(value) || 0);
  return (
    <span style={{ display: "inline-flex", gap: 1, lineHeight: 1 }} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ color: i <= full ? "#f59e0b" : "#d1d5db", fontSize: size }}>★</span>
      ))}
    </span>
  );
}

export function RatingSummary({ avg = 0, count = 0, size = 14, h }) {
  if (!count) return <span style={{ color: h ? h.sc : "#8e8e93", fontSize: 12 }}>No reviews yet</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <Stars value={avg} size={size} />
      <span style={{ fontWeight: 700, fontSize: size, color: h ? h.tx : "#111" }}>{Number(avg).toFixed(1)}</span>
      <span style={{ color: h ? h.sc : "#8e8e93", fontSize: size - 2 }}>({count})</span>
    </span>
  );
}

function ReviewItem({ r, h }) {
  return (
    <div style={{ borderTop: `1px solid ${h.bd}`, padding: "10px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <Stars value={r.rating} size={13} />
        <span style={{ color: h.sc, fontSize: 10 }}>{r.by}{r.at ? " · " + String(r.at).slice(0, 10) : ""}</span>
      </div>
      {r.body ? <div style={{ color: h.tx, fontSize: 12, lineHeight: 1.5 }}>{r.body}</div> : null}
      {r.response ? <div style={{ color: h.sc, fontSize: 11, marginTop: 4, fontStyle: "italic", paddingLeft: 8, borderLeft: `2px solid ${h.bd}` }}>Reply: {r.response}</div> : null}
    </div>
  );
}

// Public landlord-reputation block for the marketplace property detail —
// fetches the landlord's aggregate rating + published written reviews so a
// PROSPECTIVE tenant can vet the owner before inquiring. Works unauthenticated
// (the endpoint is public). Renders nothing while loading / on error.
export function LandlordReviews({ orgId, h, t, max = 5 }) {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    api.getLandlordReviews(orgId).then(d => { if (alive && d && d.success) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [orgId]);
  if (!data) return null;
  const reviews = data.reviews || [];
  const shown = showAll ? reviews : reviews.slice(0, max);
  return (
    <div style={{ background: h.cd, borderRadius: 12, padding: 14, border: `1px solid ${h.bd}`, marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: reviews.length ? 8 : 0 }}>
        <span style={{ color: h.tx, fontSize: 13, fontWeight: 700 }}>{(t && t.landlordReviews) || "Landlord Reviews"}</span>
        <RatingSummary avg={data.avg || 0} count={data.count || 0} h={h} size={14} />
      </div>
      {(!data.count) && <div style={{ color: h.sc, fontSize: 12 }}>{(t && t.noLandlordReviews) || "No reviews yet for this landlord."}</div>}
      {shown.map(r => <ReviewItem key={r.id} r={r} h={h} />)}
      {reviews.length > max && !showAll && (
        <button onClick={() => setShowAll(true)} style={{ marginTop: 8, background: "none", border: "none", color: h.ac, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}>
          {`Show all ${reviews.length} reviews`}
        </button>
      )}
    </div>
  );
}

// subject = { type:'tenant'|'landlord', phone?, orgId?, name }
export function WriteReviewModal({ h, t, subject, flash, onClose, onDone }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    if (!rating) { flash && flash("Please pick a star rating first", "error"); return; }
    setBusy(true);
    const payload = subject.type === "tenant"
      ? { subjectType: "tenant", subjectPhone: subject.phone, rating, body }
      : { subjectType: "landlord", subjectOrgId: subject.orgId, rating, body };
    try {
      const d = await api.submitReview(payload);
      if (d && d.success) { flash && flash("Review submitted ✓"); onDone && onDone(); onClose(); }
      else { flash && flash(d?.detail || d?.error || "Could not submit review", "error"); setBusy(false); }
    } catch (e) { flash && flash("Error submitting review", "error"); setBusy(false); }
  };
  const who = subject.name || (subject.type === "tenant" ? "this tenant" : "your landlord");
  return (
    <div style={overlay} onClick={onClose}>
      <div style={card(h)} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: h.tx, fontSize: 17, fontWeight: 700, margin: "0 0 4px" }}>Rate {who}</h3>
        <p style={{ color: h.sc, fontSize: 12, margin: "0 0 14px" }}>
          {subject.type === "tenant" ? "Help other landlords by sharing your experience." : "Help other tenants by sharing your experience."}
        </p>
        <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 14 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <span key={i}
              onClick={() => setRating(i)}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(0)}
              style={{ cursor: "pointer", fontSize: 34, color: i <= (hover || rating) ? "#f59e0b" : "#d1d5db", transition: "color .1s" }}>★</span>
          ))}
        </div>
        <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={1500}
          placeholder="Share details (optional) — payment habits, property care, communication…"
          style={{ width: "100%", minHeight: 90, padding: 12, borderRadius: 12, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", marginBottom: 14 }} />
        <button onClick={submit} disabled={busy}
          style={{ width: "100%", padding: 13, borderRadius: 12, border: "none", background: busy ? h.sc : h.ac, color: "#fff", fontSize: 14, fontWeight: 700, cursor: busy ? "wait" : "pointer", marginBottom: 8 }}>
          {busy ? "Submitting…" : "Submit Review"}
        </button>
        <button onClick={onClose} style={{ width: "100%", padding: 10, borderRadius: 12, border: "none", background: "transparent", color: h.sc, fontSize: 13, cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );
}

// Landlord-only: look up a (prospective or current) tenant's reputation.
export function TenantCheckModal({ h, t, flash, onClose }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showRate, setShowRate] = useState(false);
  const lookup = async () => {
    const p = phone.replace(/\D/g, "");
    if (p.length < 10) { flash && flash("Enter a valid 10-digit phone number", "error"); return; }
    setBusy(true); setResult(null);
    try {
      const d = await api.getTenantReputation(p);
      if (d && d.success) setResult(d);
      else flash && flash(d?.error || "Lookup failed", "error");
    } catch (e) { flash && flash("Lookup failed", "error"); }
    setBusy(false);
  };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={card(h)} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: h.tx, fontSize: 17, fontWeight: 700, margin: "0 0 4px" }}>🔍 Check Tenant Reputation</h3>
        <p style={{ color: h.sc, fontSize: 12, margin: "0 0 14px", lineHeight: 1.5 }}>
          Enter a tenant's phone number to see how past landlords rated them before you assign a property.
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <input value={phone} onChange={e => setPhone(e.target.value)} inputMode="tel" placeholder="Tenant phone number"
            onKeyDown={e => { if (e.key === "Enter") lookup(); }}
            style={{ flex: 1, padding: 12, borderRadius: 12, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 14, boxSizing: "border-box" }} />
          <button onClick={lookup} disabled={busy}
            style={{ padding: "0 18px", borderRadius: 12, border: "none", background: busy ? h.sc : h.ac, color: "#fff", fontSize: 14, fontWeight: 700, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "…" : "Check"}
          </button>
        </div>
        {result && (
          <div style={{ background: h.cd, borderRadius: 12, padding: 14, border: `1px solid ${h.bd}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: result.count ? 8 : 0 }}>
              <span style={{ color: h.sc, fontSize: 12, fontWeight: 600 }}>Reputation</span>
              <RatingSummary avg={result.avg} count={result.count} h={h} size={15} />
            </div>
            {result.count === 0 && <div style={{ color: h.sc, fontSize: 12 }}>No reviews yet — this tenant hasn't been rated by any landlord.</div>}
            {result.count > 0 && !result.detailUnlocked && (
              <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, marginTop: 6, padding: "8px 10px", background: `${h.ac}10`, borderRadius: 8 }}>
                Showing the overall rating only. The written reviews unlock once this tenant has applied to or been invited by you.
              </div>
            )}
            {result.detailUnlocked && result.reviews.map(r => <ReviewItem key={r.id} r={r} h={h} />)}
          </div>
        )}
        {result && result.detailUnlocked && (
          <button onClick={() => setShowRate(true)}
            style={{ width: "100%", padding: 12, borderRadius: 12, border: `1px solid ${h.ac}`, background: "transparent", color: h.ac, fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: 12 }}>
            ★ Rate this tenant
          </button>
        )}
        <button onClick={onClose} style={{ width: "100%", padding: 10, borderRadius: 12, border: "none", background: "transparent", color: h.sc, fontSize: 13, cursor: "pointer", marginTop: 8 }}>Close</button>
      </div>
      {showRate && (
        <WriteReviewModal h={h} t={t} flash={flash}
          subject={{ type: "tenant", phone: phone.replace(/\D/g, ""), name: "this tenant" }}
          onClose={() => setShowRate(false)}
          onDone={() => { setShowRate(false); lookup(); }} />
      )}
    </div>
  );
}

// Reviews for a SPECIFIC, already-known tenant — opened from the landlord's
// tenant detail page. Same reputation view as TenantCheckModal but pre-loaded
// (no phone lookup). Written reviews show when "detail unlocked" (this landlord
// has a tenancy with them, which is true on their own tenant's page); a Rate
// button is always offered. onRated fires after a successful submit so the
// caller can refresh any inline rating it shows.
export function TenantReviewsModal({ phone, name, h, t, flash, onClose, onRated }) {
  const [data, setData] = useState(null);
  const [showRate, setShowRate] = useState(false);
  const ph = String(phone || "").replace(/\D/g, "");
  const load = () => api.getTenantReputation(ph).then(d => { if (d && d.success) setData(d); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ph]);
  return (
    <div style={overlay} onClick={onClose}>
      <div style={card(h)} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: h.tx, fontSize: 17, fontWeight: 700, margin: "0 0 4px" }}>{name || "Tenant"} — Reviews</h3>
        <p style={{ color: h.sc, fontSize: 12, margin: "0 0 14px" }}>How past landlords have rated this tenant.</p>
        {!data ? (
          <div style={{ color: h.sc, fontSize: 12, padding: "8px 0" }}>Loading…</div>
        ) : (
          <div style={{ background: h.cd, borderRadius: 12, padding: 14, border: `1px solid ${h.bd}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: data.count ? 8 : 0 }}>
              <span style={{ color: h.sc, fontSize: 12, fontWeight: 600 }}>Reputation</span>
              <RatingSummary avg={data.avg} count={data.count} h={h} size={15} />
            </div>
            {data.count === 0 && <div style={{ color: h.sc, fontSize: 12 }}>No reviews yet — be the first to rate this tenant.</div>}
            {data.count > 0 && !data.detailUnlocked && (
              <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, marginTop: 6, padding: "8px 10px", background: `${h.ac}10`, borderRadius: 8 }}>
                Showing the overall rating only. Written reviews unlock once this tenant is in your org.
              </div>
            )}
            {data.detailUnlocked && (data.reviews || []).map(r => <ReviewItem key={r.id} r={r} h={h} />)}
          </div>
        )}
        {/* Only the tenant's own landlord can rate — detailUnlocked means a
            tenancy exists in this org (server re-checks on submit too). */}
        {data && data.detailUnlocked && <button onClick={() => setShowRate(true)}
          style={{ width: "100%", padding: 12, borderRadius: 12, border: `1px solid ${h.ac}`, background: "transparent", color: h.ac, fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: 12 }}>
          ★ Rate this tenant
        </button>}
        <button onClick={onClose} style={{ width: "100%", padding: 10, borderRadius: 12, border: "none", background: "transparent", color: h.sc, fontSize: 13, cursor: "pointer", marginTop: 8 }}>Close</button>
      </div>
      {showRate && (
        <WriteReviewModal h={h} t={t} flash={flash}
          subject={{ type: "tenant", phone: ph, name: name || "this tenant" }}
          onClose={() => setShowRate(false)}
          onDone={() => { setShowRate(false); load(); onRated && onRated(); }} />
      )}
    </div>
  );
}

// Compact inline tenant-reputation block — used in the Add-Tenant phone lookup
// so a landlord can vet a prospect in the SAME step (merges the old standalone
// "Check Tenant Reputation" screen). Aggregate shows for any prospect; written
// reviews unlock only once they're this landlord's tenant. Renders nothing
// until a 10-digit phone resolves.
export function TenantReputationInline({ phone, h }) {
  const [data, setData] = useState(null);
  const ph = String(phone || "").replace(/\D/g, "");
  useEffect(() => {
    if (ph.length < 10) { setData(null); return; }
    let alive = true;
    api.getTenantReputation(ph).then(d => { if (alive && d && d.success) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [ph]);
  if (!data) return null;
  return (
    <div style={{ marginBottom: 10, padding: "10px 12px", background: h.cd, borderRadius: 10, border: `1px solid ${h.bd}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: (data.count && data.detailUnlocked) ? 6 : 0 }}>
        <span style={{ color: h.sc, fontSize: 11, fontWeight: 600 }}>Tenant reputation</span>
        <RatingSummary avg={data.avg || 0} count={data.count || 0} h={h} size={13} />
      </div>
      {data.count > 0 && !data.detailUnlocked && <div style={{ color: h.ts, fontSize: 10, lineHeight: 1.4 }}>Overall rating from past landlords. Written reviews unlock once they're your tenant.</div>}
      {data.detailUnlocked && (data.reviews || []).slice(0, 3).map(r => <ReviewItem key={r.id} r={r} h={h} />)}
    </div>
  );
}
